import { api } from '../../core/apiClient';
import {
  PlanningResource, PlanningResourceCalendarException, PlanningPhase, PlanningResourceNeed,
  PlanningAssignment, PlanningConflict, PlanningPhaseTemplate, PlanningPhaseTemplateItem,
  PlanningPhaseSegment, PlanningResourceOption,
} from './types';

type Envelope<T> = { success: boolean; data: T; message?: string };

async function unwrap<T>(p: Promise<Envelope<T>>): Promise<T> {
  const res = await p;
  return res.data;
}

export const planningApi = {
  // Recursos
  listResources: (type?: string, active: 'all' | 'active' = 'active') =>
    unwrap<PlanningResource[]>(api.get(`/planning/resources?active=${active}${type ? `&type=${type}` : ''}`)),
  createResource: (dto: Partial<PlanningResource>) => unwrap<PlanningResource>(api.post('/planning/resources', dto)),
  updateResource: (id: string, dto: Partial<PlanningResource>) => unwrap<PlanningResource>(api.put(`/planning/resources/${id}`, dto)),
  removeResource: (id: string) => unwrap<null>(api.delete(`/planning/resources/${id}`)),
  listResourceOptions: (type: string, params: { resourceId?: string; search?: string } = {}) => {
    const qs = new URLSearchParams({ type, ...(params.resourceId ? { resourceId: params.resourceId } : {}), ...(params.search ? { search: params.search } : {}) }).toString();
    return unwrap<PlanningResourceOption[]>(api.get(`/planning/resources/options?${qs}`));
  },

  listCalendarExceptions: (resourceId: string) =>
    unwrap<PlanningResourceCalendarException[]>(api.get(`/planning/resources/${resourceId}/calendar-exceptions`)),
  addCalendarException: (resourceId: string, dto: Partial<PlanningResourceCalendarException>) =>
    unwrap<PlanningResourceCalendarException>(api.post(`/planning/resources/${resourceId}/calendar-exceptions`, dto)),
  removeCalendarException: (resourceId: string, id: string) =>
    unwrap<null>(api.delete(`/planning/resources/${resourceId}/calendar-exceptions/${id}`)),

  // Plantilles
  // Necessitats de diverses fases en una sola crida (per pintar badges de
  // cobertura a CronogramaGrid/GeneralTab sense fer una crida per fase).
  listNeedsBulk: (phaseIds: string[]) =>
    phaseIds.length === 0 ? Promise.resolve([]) : unwrap<PlanningResourceNeed[]>(api.get(`/planning/phases/needs?phaseIds=${phaseIds.join(',')}`)),

  listTemplates: () => unwrap<PlanningPhaseTemplate[]>(api.get('/planning/phase-templates')),
  getTemplate: (id: string) => unwrap<PlanningPhaseTemplate>(api.get(`/planning/phase-templates/${id}`)),
  createTemplate: (dto: { name: string; description?: string }) => unwrap<PlanningPhaseTemplate>(api.post('/planning/phase-templates', dto)),
  removeTemplate: (id: string) => unwrap<null>(api.delete(`/planning/phase-templates/${id}`)),
  addTemplateItem: (templateId: string, dto: Partial<PlanningPhaseTemplateItem>) =>
    unwrap<PlanningPhaseTemplateItem>(api.post(`/planning/phase-templates/${templateId}/items`, dto)),
  removeTemplateItem: (templateId: string, itemId: string) =>
    unwrap<null>(api.delete(`/planning/phase-templates/${templateId}/items/${itemId}`)),
  generateFromTemplate: (templateId: string, dto: { expedientCode?: string; expedientLabel?: string; startDate: string }) =>
    unwrap<PlanningPhase[]>(api.post(`/planning/phase-templates/${templateId}/generate`, dto)),

  // Força una sincronització de totes les fases des del Cronograma de cada
  // expedient (backfill / reparació manual — vegeu planningSync.service.ts).
  syncAllFromCronograma: () => unwrap<{ projectsSynced: number }>(api.post('/planning/phases/sync-all', {})),

  // Fases
  listPhases: (params: { expedientCode?: string; status?: string; dateFrom?: string; dateTo?: string; search?: string } = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString();
    return unwrap<PlanningPhase[]>(api.get(`/planning/phases${qs ? `?${qs}` : ''}`));
  },
  createPhase: (dto: Partial<PlanningPhase>) => unwrap<PlanningPhase>(api.post('/planning/phases', dto)),
  updatePhase: (id: string, dto: Partial<PlanningPhase>) => unwrap<PlanningPhase>(api.put(`/planning/phases/${id}`, dto)),
  removePhase: (id: string) => unwrap<null>(api.delete(`/planning/phases/${id}`)),
  rebaselinePhase: (id: string) => unwrap<PlanningPhase>(api.post(`/planning/phases/${id}/rebaseline`, {})),
  updatePhaseSegments: (id: string, segments: PlanningPhaseSegment[]) =>
    unwrap<PlanningPhase>(api.patch(`/planning/phases/${id}/segments`, { segments })),
  getChapterOrder: (expedientCode: string) =>
    unwrap<string[]>(api.get(`/planning/phases/chapter-order?expedientCode=${encodeURIComponent(expedientCode)}`)),
  setChapterOrder: (expedientCode: string, order: string[]) =>
    unwrap<string[]>(api.put('/planning/phases/chapter-order', { expedientCode, order })),
  renameChapter: (expedientCode: string, oldChapter: string | null, newChapter: string) =>
    unwrap<null>(api.patch('/planning/phases/chapters/rename', { expedientCode, oldChapter, newChapter })),
  cascadePreview: (id: string, newStartDate: string) =>
    unwrap<{ phaseId: string; name: string; currentStartDate: string; currentEndDate: string; proposedStartDate: string; proposedEndDate: string; skippedFixed: boolean }[]>(
      api.get(`/planning/phases/${id}/cascade-preview?newStartDate=${newStartDate}`)
    ),

  listNeeds: (phaseId: string) => unwrap<PlanningResourceNeed[]>(api.get(`/planning/phases/${phaseId}/needs`)),
  addNeed: (phaseId: string, dto: Partial<PlanningResourceNeed>) => unwrap<PlanningResourceNeed>(api.post(`/planning/phases/${phaseId}/needs`, dto)),
  updateNeed: (phaseId: string, needId: string, dto: Partial<PlanningResourceNeed>) =>
    unwrap<PlanningResourceNeed>(api.put(`/planning/phases/${phaseId}/needs/${needId}`, dto)),
  removeNeed: (phaseId: string, needId: string) => unwrap<null>(api.delete(`/planning/phases/${phaseId}/needs/${needId}`)),

  // Assignacions
  listAssignments: (params: { resourceId?: string; phaseId?: string; phaseIds?: string[]; dateFrom?: string; dateTo?: string; status?: string } = {}) => {
    if (params.phaseIds && params.phaseIds.length === 0) return Promise.resolve([]);
    const { phaseIds, ...rest } = params;
    const qs = new URLSearchParams(Object.entries(rest).filter(([, v]) => v) as [string, string][]);
    if (phaseIds && phaseIds.length > 0) qs.set('phaseIds', phaseIds.join(','));
    const s = qs.toString();
    return unwrap<PlanningAssignment[]>(api.get(`/planning/assignments${s ? `?${s}` : ''}`));
  },
  checkConflicts: (dto: { resourceId: string; startDate: string; endDate: string; segments?: PlanningPhaseSegment[]; startTime?: string | null; endTime?: string | null; ignoreAssignmentId?: string; phaseId?: string; hours?: number }) =>
    unwrap<PlanningConflict[]>(api.post('/planning/assignments/check-conflicts', dto)),
  createAssignment: (dto: Omit<Partial<PlanningAssignment>, 'conflictOverride'> & { conflictOverride?: boolean; conflictOverrideReason?: string }) =>
    unwrap<PlanningAssignment>(api.post('/planning/assignments', dto)),
  updateAssignment: (id: string, dto: Omit<Partial<PlanningAssignment>, 'conflictOverride'> & { conflictOverride?: boolean; conflictOverrideReason?: string }) =>
    unwrap<PlanningAssignment>(api.put(`/planning/assignments/${id}`, dto)),
  removeAssignment: (id: string) => unwrap<null>(api.delete(`/planning/assignments/${id}`)),
};
